import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import logoImg from '@/assets/logo.png';

interface SignupFormData {
  companyName: string;
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export default function SignupPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<SignupFormData>({
    companyName: '',
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const validateForm = (): string | null => {
    if (!formData.companyName.trim()) return 'Nome da empresa é obrigatório';
    if (!formData.name.trim()) return 'Seu nome é obrigatório';
    if (!formData.email.trim()) return 'Email é obrigatório';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Email inválido';
    if (formData.password.length < 8) return 'A senha deve ter pelo menos 8 caracteres';
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) return 'A senha deve conter letras maiúsculas, minúsculas e números';
    if (formData.password !== formData.confirmPassword) return 'As senhas não coincidem';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const validationError = validateForm();
    if (validationError) { setError(validationError); return; }
    setIsLoading(true);
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'https://o7pkyged3j.execute-api.us-east-1.amazonaws.com';
      const response = await fetch(`${API_URL}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: formData.companyName,
          name: formData.name,
          email: formData.email,
          password: formData.password,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error?.message || 'Erro ao criar conta');
      setSuccess(true);
    } catch (err: any) {
      console.error('Signup error:', err);
      setError(err.message || 'Erro ao criar conta. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof SignupFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  // Navy left panel (shared between states)
  const leftPanel = (
    <div className="hidden lg:flex lg:w-1/2 bg-[#0D1B2A] relative overflow-hidden flex-col justify-between p-12">
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-[#00C2C7]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-[#00C2C7]/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10">
        <img src={logoImg} alt="TalentumIQ" className="h-10 object-contain" />
      </div>

      <div className="relative z-10 space-y-6">
        <h1 className="text-4xl font-bold text-white leading-tight font-heading">
          Comece a recrutar<br />com <span className="text-[#00C2C7]">inteligência</span>.
        </h1>
        <p className="text-gray-400 text-lg max-w-md leading-relaxed">
          Crie sua conta gratuita e tenha acesso a análise de currículos com IA, entrevistas pontuadas e um assistente inteligente.
        </p>

        <div className="mt-8 space-y-4">
          {[
            { icon: '📄', text: 'Análise automática de currículos' },
            { icon: '🎯', text: 'Score de compatibilidade por vaga' },
            { icon: '🧠', text: 'Assistente IA com seus dados' },
            { icon: '🔗', text: 'Integração com LinkedIn' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-3">
              <span className="text-lg">{item.icon}</span>
              <span className="text-gray-300 text-sm">{item.text}</span>
            </div>
          ))}
        </div>
      </div>

      <p className="relative z-10 text-gray-600 text-sm">
        © {new Date().getFullYear()} TalentumIQ · Powered by Druid
      </p>
    </div>
  );

  if (success) {
    return (
      <div className="min-h-screen flex">
        {leftPanel}
        <div className="flex-1 flex flex-col items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-blue-50/30 to-cyan-50/20">
          <div className="w-full max-w-md">
            <Card className="shadow-lg border-0 shadow-black/5">
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl font-bold text-center text-green-600 font-heading">
                  Conta Criada com Sucesso!
                </CardTitle>
                <CardDescription className="text-center">
                  Verifique seu email para ativar sua conta
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800">
                    Enviamos um email de verificação para <strong>{formData.email}</strong>.
                    Clique no link do email para ativar sua conta e começar a usar o TalentumIQ.
                  </p>
                </div>
                <div className="text-center text-sm text-muted-foreground">
                  Não recebeu o email?{' '}
                  <button className="text-primary hover:underline">Reenviar</button>
                </div>
                <Button variant="outline" className="w-full" onClick={() => navigate('/login')}>
                  Voltar para Login
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {leftPanel}

      <div className="flex-1 flex flex-col items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-blue-50/30 to-cyan-50/20">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 lg:hidden">
            <img src={logoImg} alt="TalentumIQ" className="h-12 object-contain mx-auto mb-2" />
            <p className="text-muted-foreground">Recrutamento inteligente, decisões precisas</p>
          </div>

          <div className="hidden lg:block text-center mb-8">
            <h2 className="text-2xl font-bold font-heading text-foreground">Crie sua conta</h2>
            <p className="text-muted-foreground mt-1">Comece a usar o TalentumIQ gratuitamente</p>
          </div>

          <Card className="shadow-lg border-0 shadow-black/5">
            <CardHeader className="space-y-1 lg:hidden">
              <CardTitle className="text-xl text-center font-heading">Criar Conta</CardTitle>
              <CardDescription className="text-center">Comece a usar o TalentumIQ gratuitamente</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">{error}</div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="companyName">Nome da Empresa</Label>
                  <Input id="companyName" type="text" placeholder="Sua Empresa Ltda" value={formData.companyName} onChange={handleChange('companyName')} required disabled={isLoading} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Seu Nome</Label>
                  <Input id="name" type="text" placeholder="João Silva" value={formData.name} onChange={handleChange('name')} required disabled={isLoading} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" placeholder="joao@empresa.com" value={formData.email} onChange={handleChange('email')} required disabled={isLoading} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Senha</Label>
                  <Input id="password" type="password" placeholder="••••••••" value={formData.password} onChange={handleChange('password')} required disabled={isLoading} />
                  <p className="text-xs text-muted-foreground">Mínimo 8 caracteres, com letras maiúsculas, minúsculas e números</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmar Senha</Label>
                  <Input id="confirmPassword" type="password" placeholder="••••••••" value={formData.confirmPassword} onChange={handleChange('confirmPassword')} required disabled={isLoading} />
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Criando conta...' : 'Criar Conta'}
                </Button>
              </form>
              <div className="mt-4 text-center text-sm text-muted-foreground">
                Já tem uma conta?{' '}
                <a href="/login" className="text-primary hover:underline font-medium">Fazer login</a>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-muted-foreground mt-6 lg:hidden">
            Powered by <span className="font-medium">Druid</span> · © {new Date().getFullYear()} TalentumIQ
          </p>
        </div>
      </div>
    </div>
  );
}
